import './copy-text-area.scss';

import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { useAppContext } from '../../../data/context/app-context';
import {
  ConversationSummaryMessageInput,
  TransformConversationSummaryDocument,
} from '../../../data/types/api/graphql';
import { AlertMessageType } from '../../alert-message/alert-message';
import { FilterButton } from '../../filter-button/filter-button';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';
import { CopyTextArea } from './copy-text-area';

type CopyTextAreaWithFilterProps = {
  text: string;
  messages: ConversationSummaryMessageInput[];
  customPrompt?: string;
  className?: string;
  onCopy?: () => void;
};

export function CopyTextAreaWithFilter({
  text,
  messages,
  customPrompt = 'Summarize and simplify this conversation to be easy to understand',
  className,
  onCopy,
}: CopyTextAreaWithFilterProps) {
  const [filterMode, setFilterMode] = useState<'original' | 'simplified'>(
    'original'
  );
  const [aiText, setAiText] = useState<string | null>(null);
  const { showAlertMessage } = useAppContext();
  const [transformConversationSummary, { loading: transformLoading }] =
    useLazyQuery(TransformConversationSummaryDocument);

  useEffect(() => {
    setFilterMode('original');
    setAiText(null);
  }, [text]);

  const handleFilterOriginal = useCallback(() => {
    setFilterMode('original');
  }, []);

  const handleFilterSimplified = useCallback(async () => {
    setFilterMode('simplified');
    if (aiText) return;
    try {
      const result = await transformConversationSummary({
        variables: { messages, customPrompt },
      });
      setAiText(result.data?.conversationSummarize ?? '');
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    aiText,
    messages,
    customPrompt,
    transformConversationSummary,
    showAlertMessage,
  ]);

  const displayText = filterMode === 'simplified' && aiText ? aiText : text;

  return (
    <div className="copy-text-area-with-filter">
      <CopyTextArea
        text={displayText}
        disabled={transformLoading}
        className={className}
        onCopy={onCopy}
      />
      {messages.length > 0 && (
        <div className="copy-text-area-with-filter__filters">
          <FilterButton
            active={filterMode === 'original'}
            onClick={handleFilterOriginal}
          >
            <CustomFormattedMessage id="common.filter.original-text" />
          </FilterButton>
          <FilterButton
            active={filterMode === 'simplified'}
            loading={transformLoading}
            onClick={handleFilterSimplified}
          >
            <CustomFormattedMessage id="common.filter.ai-text" />
          </FilterButton>
        </div>
      )}
    </div>
  );
}
